import React, { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { testimonials } from "../data/mock";

const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const nextTestimonial = () => {
    if (!isAnimating) {
      setIsAnimating(true);
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
      setTimeout(() => setIsAnimating(false), 500);
    }
  };

  const prevTestimonial = () => {
    if (!isAnimating) {
      setIsAnimating(true);
      setCurrentIndex(
        (prev) => (prev - 1 + testimonials.length) % testimonials.length
      );
      setTimeout(() => setIsAnimating(false), 500);
    }
  };

  const goToTestimonial = (index) => {
    if (!isAnimating && index !== currentIndex) {
      setIsAnimating(true);
      setCurrentIndex(index);
      setTimeout(() => setIsAnimating(false), 500);
    }
  };

  // Auto-play functionality
  useEffect(() => {
    const interval = setInterval(() => {
      nextTestimonial();
    }, 7000);
    return () => clearInterval(interval);
  }, [currentIndex]);

  const currentTestimonial = testimonials[currentIndex];

  return (
    <section id="testimonials" className="section-padding bg-[#FAFAF9]">
      <div className="container mx-auto px-4 sm:px-6 lg:px-16">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-20 px-2 sm:px-0">
          <div className="divider-accent mx-auto"></div>
          <h2 className="heading-1 text-[#1C1917] mb-4 sm:mb-6 mt-6">
            Patient Experiences
          </h2>
          <p className="body-large text-[#57534E] text-sm sm:text-base">
            We love hearing from our patients about their journey to confident,
            healthy smiles.
          </p>
        </div>

        {/* Testimonial Card */}
        <div className="max-w-4xl mx-auto px-2 sm:px-0">
          <div className="relative bg-[#FFFFFF] p-6 sm:p-10 lg:p-16 rounded-lg shadow-elevated border border-[#E7E5E4]">
            {/* Quote Icon */}
            <div className="absolute top-4 sm:top-10 left-4 sm:left-10 opacity-5">
              <Quote
                className="w-16 h-16 sm:w-24 sm:h-24 text-[#0F766E]"
                fill="currentColor"
              />
            </div>

            {/* Content */}
            <div className="relative z-10">
              <div
                className={`transition-opacity duration-500 ${
                  isAnimating ? "opacity-0" : "opacity-100"
                }`}
              >
                <p
                  className="text-lg sm:text-2xl font-light text-[#1C1917] mb-6 sm:mb-10 leading-relaxed min-h-[120px] sm:min-h-[140px] italic text-xs sm:text-base"
                  style={{
                    fontFamily: "Cormorant, Georgia, serif",
                    letterSpacing: "-0.02em",
                  }}
                >
                  "{currentTestimonial.text}"
                </p>

                {/* Author */}
                <div className="flex items-center gap-3 sm:gap-5 pb-6 sm:pb-10 border-b border-[#E7E5E4]">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 overflow-hidden bg-[#F5F5F4] rounded-full flex-shrink-0">
                    <img
                      src={currentTestimonial.avatar}
                      alt={currentTestimonial.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <p className="heading-3 text-[#1C1917] text-sm sm:text-base">
                      {currentTestimonial.name}
                    </p>
                    <p className="body-small text-[#78716C] uppercase tracking-wider text-xs">
                      Verified Patient
                    </p>
                  </div>
                </div>
              </div>

              {/* Navigation */}
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 sm:gap-0 mt-6 sm:mt-10">
                {/* Dots */}
                <div className="flex gap-2 sm:gap-3 flex-wrap justify-center">
                  {testimonials.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => goToTestimonial(index)}
                      className={`h-1.5 rounded-full transition-all duration-400 ${
                        index === currentIndex
                          ? "bg-[#0F766E] w-8 sm:w-10"
                          : "bg-[#D6D3D1] w-1.5 hover:bg-[#A8A29E]"
                      }`}
                      aria-label={`Go to testimonial ${index + 1}`}
                    />
                  ))}
                </div>

                {/* Arrow Buttons */}
                <div className="flex gap-2 sm:gap-3">
                  <button
                    onClick={prevTestimonial}
                    className="w-10 h-10 sm:w-12 sm:h-12 border border-[#D6D3D1] rounded-md flex items-center justify-center hover:border-[#0F766E] hover:bg-[#0F766E]/5 transition-all duration-300 flex-shrink-0"
                    aria-label="Previous testimonial"
                  >
                    <ChevronLeft
                      className="w-4 h-4 sm:w-5 sm:h-5 text-[#1C1917]"
                      strokeWidth={1.5}
                    />
                  </button>
                  <button
                    onClick={nextTestimonial}
                    className="w-10 h-10 sm:w-12 sm:h-12 border border-[#D6D3D1] rounded-md flex items-center justify-center hover:border-[#0F766E] hover:bg-[#0F766E]/5 transition-all duration-300 flex-shrink-0"
                    aria-label="Next testimonial"
                  >
                    <ChevronRight
                      className="w-4 h-4 sm:w-5 sm:h-5 text-[#1C1917]"
                      strokeWidth={1.5}
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
